<template>
	<div class="container py-5">
        <h2>{{ $t('sumarizedCostCharts') }}</h2>
        <nav aria-label="breadcrumb" class="mb-4">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="`/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}`">
                        {{ $t('modelingResult') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('sumarizedCostCharts') }}</li>
            </ol>
        </nav>
		<div v-if="pending">
            <div class="d-flex justify-content-center loading-center">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="row mb-5" v-for="cfg in chartConfigs" :key="cfg.limitId">
                <div class="col-md-8">
                    <h5 class="mb-3">
                        {{ $t('limit') }}: {{ cfg.limitName }}
                    </h5>
                    <apexchart
                        v-if="getSelectedChart(cfg)"
                        type="bar"
                        width="800"
                        :height="500"
                        :options="makeOptions(getSelectedChart(cfg), cfg.limitName)"
                        :series="colorizeSeries(getSelectedChart(cfg).series)"
                        class="d-flex justify-content-center"
                    />
                </div>
                <div class="col-md-4">
                    <label>{{ $t('area') }}</label>
                    <select class="form-select mb-4" v-model="cfg.selectedGroupId">
                        <option :value="null">{{ $t('allAreas') }}</option>
                        <option v-for="g in groups" :key="g.value" :value="Number(g.value)">
                            {{ g.name }}
                        </option>
                    </select>
                    <label>{{ $t('packages') }}</label>
                    <select class="form-select" v-model="cfg.selectedPackageId">
                        <option :value="null">{{ $t('allPackages') }}</option>
                        <option v-for="p in packages" :key="p.value" :value="Number(p.value)">
                            {{ p.name }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex mb-4">
                <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}` })">{{ $t('back') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import AnalysisApi from "../../api/server/analysis";
import ModelingApi from "../../api/server/modeling";
import CheckIfUserIsAdmin from "../../authCache";
import { formatAmount } from '../../utils/currencyUtils';

export default {
    name: "resultCharts",
    watch: {
        $route: "requestData"
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.isUserAdmin = await CheckIfUserIsAdmin();

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            var result = await AnalysisApi.sumarizedCostGraph(this.$route.params.id, this.$route.params.modelingResultId);
            this.groups = result.groups;
            this.packages = result.packages;
            this.chartConfigs = result.limits.map(limit => ({
                limitId: limit.limitId,
                limitName: limit.limitName,
                charts: limit.charts,
                selectedGroupId: null,
                selectedPackageId: null
            }));

            this.pending = false;
        },
        colorizeSeries(series) {
            return series.map((s, i) => ({
            ...s,
            color: this.generateColors(series.length)[i]  // HSL barva pro každou sérii
            }));
        },
        getSelectedChart(cfg) {
            var selectedChart  = cfg.selectedGroupId == null ? cfg.charts.find(c => c.groupId == null) : cfg.charts.find(c => c.groupId === cfg.selectedGroupId);

            if (!selectedChart)
                return null;

            if (cfg.selectedPackageId == null)
                return selectedChart;

            const filtered = {
                ...selectedChart,
                series: (selectedChart.series || []).filter(s => Number(s.packageId) === Number(cfg.selectedPackageId))
            };

            return filtered.series.length ? filtered : null;
        },
        makeOptions(chart, limitName) {
            const colors = this.generateColors(chart.series.length);

            return {
                chart: { type: "bar", height: 500 },
                title: {
                    text: `${this.$t("summarizedCostCharts")} - ${limitName}`
                },
                xaxis: {
                    categories: chart.years,
                    title: { text: this.$t("year") }
                },
                yaxis: {
                    labels: {
                        formatter: v => formatAmount(v, chart.currency)
                    },
                    title: { text: `${this.$t("totalPackageCost")} ${chart.currency}` }
                },
                tooltip: {
                    y: { formatter: v => formatAmount(v, chart.currency) }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: "50%",
                        endingShape: "rounded"
                    }
                },  
                dataLabels: { enabled: false },
                legend: { position: "top" },
                colors
            };
        },
        generateColors(count) {
            return Array.from({ length: count }, (_, i) => {
                const hue = Math.round((360 * i) / count)
                return `hsl(${hue}, 70%, 50%)`
            })
        },
    },
    data() {
        return {
            chartConfigs: [],
            assetTypeList: [],
            groups: [],
            packages: [],
            pending: true,
            isUserAdmin: false,
            modelingName: ""
        }
    }
};
</script>